import React from "react";
import { Link } from "react-router-dom";
import { SITE_EMAILS } from "../lib/siteConfig";

// Terms & privacy — public legal-style page. Single column, restrained typography.
// Content is intentionally generic / template-style so the user (Z-Cars) can hand
// it to legal and refine. Last-updated stamp keeps it honest.
const SECTIONS = [
  {
    id: "use",
    title: "1. Use of the platform",
    body: [
      "By creating an account or making a booking on Z-Cars, you agree to these terms. You must be at least 24 years old, hold a valid driver's licence, and pass our identity verification.",
      "You agree to use Z-Cars only for lawful purposes, to provide accurate information, and to keep your login credentials confidential. We may suspend or terminate accounts that violate these terms or our community guidelines.",
    ],
  },
  {
    id: "bookings",
    title: "2. Bookings and payments",
    body: [
      "When you confirm a booking, the trip total is held in escrow and released to the host after a successful return. Cancellations made at least 24 hours before pick-up receive the maximum refund: original taxes are returned, while Z-Cars retains 10% of the original platform fee and any converted Airalo SIM cost. Cancellations 6–24 hours before pick-up refund 50% of base rental plus taxes and any one-way or after-hours fees; within 6 hours the booking is non-refundable.",
      "Hosts set their own daily rates within marketplace guardrails. At checkout, trip extras (one-way return, after-hours pick-up/return) are listed separately from tax and service fees. On confirmation summaries, Taxes & fees includes tax, platform service fees, and any applicable one-way or after-hours charges so the lines add up to your total.",
    ],
  },
  {
    id: "host",
    title: "3. Host responsibilities",
    body: [
      "Hosts must maintain their vehicles in safe, clean, and roadworthy condition, with valid registration and a current safety inspection where required by local law. Vehicles must accurately match their listing — make, model, year, mileage, and any disclosed limitations.",
      "Hosts agree to honour confirmed bookings, deliver the vehicle on time at the agreed location, and complete a pre- and post-trip inspection with photos.",
    ],
  },
  {
    id: "insurance",
    title: "4. Insurance and damage",
    body: [
      "Every confirmed booking includes a per-trip protection plan covering up to $50,000 in third-party liability and damage to the rented vehicle, subject to a $1,500 deductible per incident. Coverage is void if the renter operates the vehicle outside the listed coverage region, off-road, or while under the influence.",
      "Damage claims must be filed in-app within 48 hours of trip end with supporting photos and an inspection report.",
    ],
  },
  {
    id: "data",
    title: "5. Data and privacy",
    body: [
      "We collect the minimum data needed to verify identity, process bookings, prevent fraud, and improve the marketplace. ID images are encrypted at rest and automatically purged 90 days after your last trip. We do not sell personal data to third parties.",
      "Trip telematics (location, speed, mileage) are recorded only during active trips and shared with hosts for return verification and incident handling. You can request a full export or deletion of your data at any time by emailing " + SITE_EMAILS.privacy + ".",
    ],
  },
  {
    id: "fractional",
    title: "6. Fractional ownership",
    body: [
      "Fractional ownership offerings on Z-Cars represent the right to a proportional share of net rental yield from a specific listed vehicle. Fractions do not constitute ownership of the underlying physical vehicle and are non-transferable until a secondary market is enabled.",
      "Yield distributions depend on trip volume and operating costs and are not guaranteed. Past performance is not indicative of future returns. Consult a financial advisor before investing.",
    ],
  },
  {
    id: "liability",
    title: "7. Limitation of liability",
    body: [
      "Z-Cars is a marketplace connecting hosts and renters. To the fullest extent permitted by law, we are not liable for indirect, incidental, or consequential damages arising from your use of the platform. Our total liability for any claim is limited to the amount you paid Z-Cars in the 12 months preceding the claim.",
    ],
  },
  {
    id: "changes",
    title: "8. Changes to these terms",
    body: [
      "We may update these terms from time to time. Material changes will be communicated by email and via an in-app notice at least 14 days before they take effect. Continued use of Z-Cars after that date constitutes acceptance.",
    ],
  },
];

export default function Terms() {
  return (
    <div className="max-w-[920px] mx-auto px-6 lg:px-10 py-16" data-testid="terms-page">
      <div className="eyebrow mb-3">Legal</div>
      <h1 className="zc-headline text-5xl md:text-6xl tracking-tight">Terms &amp; privacy.</h1>
      <p className="text-[var(--zc-text-2)] mt-4 max-w-2xl">
        Plain-language summary of what we collect, why, and your obligations as a renter, host, or investor.
        For the full legal text, contact{" "}
        <a href={`mailto:${SITE_EMAILS.support}`} className="underline hover:text-[var(--zc-text)]">{SITE_EMAILS.support}</a>.
      </p>
      <div className="text-xs text-[var(--zc-text-3)] mt-3">Last updated: 17 July 2026</div>

      {/* Table of contents */}
      <nav className="mt-10 zc-card p-5">
        <div className="eyebrow mb-3">Contents</div>
        <ol className="space-y-2 text-sm">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-[var(--zc-secondary)] hover:underline">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-12 space-y-12">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-24">
            <h2 className="zc-headline text-2xl md:text-3xl">{s.title}</h2>
            <div className="mt-4 space-y-4 text-sm text-[var(--zc-text-2)] leading-relaxed">
              {s.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-16 zc-card p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="zc-headline text-lg">Questions about your data?</div>
          <p className="text-sm text-[var(--zc-text-2)] mt-1">
            We respond to privacy requests within 30 days as required by law.
          </p>
        </div>
        <Link to="/help" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm" data-testid="terms-help-cta">
          Visit help center
        </Link>
      </div>
    </div>
  );
}
